import React, {Component, createRef} from 'react';
import cx from 'classnames';
import ResizeObserver from 'resize-observer-polyfill';
import {eventPrevented} from '../../../../../../utils/eventPrevented';
import styles from './styles.module.scss';

export enum Direction {
    horizontal = 'horizontal',
    vertical = 'vertical'
}

type Props = {
    direction: Direction; // Currently we support only horizontal direction
    getAnchorEl: () => HTMLDivElement | null;
    componentClasses?: string;
    sidePadding?: number | 'auto';
    height?: number | 'auto';
    scrollableSize?: number;
    isRtl: boolean;
    showArrows?: boolean;
};

type State = {
    railWidth: number;
    handleSize: number;
    isDragging: boolean;
};

export enum ScrollDirection {
    backward = '0',
    forward = '1'
}

export const scrollStepX = 400;
export const scrollStepY = 200;

export class Scroller extends Component<Props, State> {
    componentRef = createRef<HTMLDivElement>();
    railRef = createRef<HTMLDivElement>();
    handleRef = createRef<HTMLDivElement>();
    anchorEl: HTMLDivElement | null = null;
    observer: ResizeObserver | null = null;
    isHorizontal: boolean = false;

    initialOffset: number = 0;
    scrollStartPosition: number = 0;

    constructor(props) {
        super(props);

        this.isHorizontal = props.direction === Direction.horizontal;
        this.state = {
            railWidth: 0,
            handleSize: 0,
            isDragging: false
        };
    }

    componentDidMount() {
        const ref = this.componentRef.current;
        this.anchorEl = this.props.getAnchorEl();

        if (ref && this.railRef.current && this.anchorEl) {
            this.observer = new ResizeObserver(() => {
                this.handleResize(ref);
                this.handleThumbPosition();
            });
            this.observer.observe(ref);

            this.anchorEl.addEventListener('scroll', () => this.handleThumbPosition());
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
        if (this.componentRef.current && prevProps.scrollableSize !== this.props.scrollableSize) {
            // update position on scrollable width change
            this.setOriginalScrollOffset();
            this.handleThumbPosition();
            this.handleResize(this.componentRef.current);
        }
    }

    componentWillUnmount() {
        this.observer?.unobserve(this.componentRef.current);
        if (this.anchorEl) {
            this.anchorEl.removeEventListener('scroll', () => this.handleThumbPosition());
        }
    }

    handleResize(ref: HTMLDivElement) {
        const railRef = this.railRef.current;
        if (railRef) {
            const clientSize = this.isHorizontal ? ref.clientWidth : ref.clientHeight;
            const trackSize = this.isHorizontal ? railRef.clientWidth : railRef.clientHeight;
            const scrollSize = this.props.scrollableSize;
            this.setState({handleSize: Math.max((clientSize / scrollSize) * trackSize, 20)});
        }
    }

    handleThumbPosition = () => {
        if (!this.railRef.current || !this.handleRef.current || !this.anchorEl) return;

        if (this.isHorizontal) {
            const {scrollLeft} = this.anchorEl;
            const railClientWidth = this.railRef.current.clientWidth;

            const newPos = Math.min(
                (scrollLeft / this.props.scrollableSize) * railClientWidth,
                railClientWidth - this.state.handleSize
            );

            this.handleRef.current.style.left = `${newPos}px`;
        } else {
            const {scrollTop} = this.anchorEl;
            const railClientHeight = this.railRef.current.clientHeight;
            const newPos = Math.min(
                (scrollTop / this.props.scrollableSize) * railClientHeight,
                railClientHeight - this.state.handleSize
            );
            this.handleRef.current.style.top = `${newPos}px`;
        }
    };

    onRailClick = e => {
        eventPrevented(e, true);
        const trackCurrent = this.railRef.current;

        if (trackCurrent && this.anchorEl) {
            const {isRtl} = this.props;
            const rect = e.target.getBoundingClientRect();
            const thumbOffset = -(this.state.handleSize / 2);

            if (this.isHorizontal) {
                const clickOffset = isRtl
                    ? e.clientX - rect.right - thumbOffset
                    : e.clientX - rect.left + thumbOffset;
                const clickRatio = clickOffset / trackCurrent.clientWidth;

                const scrollAmount = Math.floor(clickRatio * this.anchorEl.scrollWidth);
                this.anchorEl.scrollTo({left: scrollAmount, behavior: 'smooth'});
            } else {
                const clickRatio = (e.clientY - rect.top + thumbOffset) / trackCurrent.clientHeight;
                const scrollAmount = Math.floor(clickRatio * this.anchorEl.scrollHeight);
                this.anchorEl.scrollTo({top: scrollAmount, behavior: 'smooth'});
            }
        }
    };

    setOriginalScrollOffset = () => {
        const scrollProp = this.isHorizontal ? 'scrollLeft' : 'scrollTop';
        this.initialOffset = this.anchorEl?.[scrollProp] || 0;
    };

    handleThumbMousedown = e => {
        eventPrevented(e, true);
        this.scrollStartPosition = this.isHorizontal ? e.clientX : e.clientY;
        this.setOriginalScrollOffset();

        this.setState({isDragging: true});

        document.addEventListener('mousemove', this.handleThumbMousemove);
        document.addEventListener('mouseup', this.handleThumbMouseup);
    };

    handleThumbMouseup = e => {
        eventPrevented(e, true);
        if (this.state.isDragging) this.setState({isDragging: false});

        document.removeEventListener('mousemove', this.handleThumbMousemove);
        document.removeEventListener('mouseup', this.handleThumbMouseup);
    };

    handleThumbMousemove = e => {
        eventPrevented(e, true);
        const {isDragging, handleSize} = this.state;
        const {scrollableSize} = this.props;

        if (isDragging && this.anchorEl) {
            if (this.isHorizontal) {
                const {offsetWidth: contentOffsetWidth} = this.anchorEl;
                const delta =
                    (e.clientX - this.scrollStartPosition) * (contentOffsetWidth / handleSize);

                this.anchorEl.scrollLeft = Math.min(
                    this.initialOffset + delta,
                    scrollableSize - contentOffsetWidth
                );
            } else {
                const {clientHeight, scrollHeight} = this.anchorEl;

                const delta = e.clientY - this.scrollStartPosition;
                this.anchorEl.scrollTop =
                    this.initialOffset + delta / (clientHeight / scrollHeight);
            }
        }
    };

    getHandleSize(clientSize: number, scrollSize: number, trackSize: number) {
        return Math.max((clientSize / scrollSize) * trackSize, 20);
    }

    onArrowClick = e => {
        e.preventDefault();
        const {getAnchorEl, isRtl} = this.props;
        const direction = e.target.getAttribute('data-direction');
        const current = getAnchorEl();

        if (current) {
            if (this.isHorizontal) {
                const scrollForward = direction === ScrollDirection.forward;
                let scrollAmount = scrollForward ? scrollStepX : -scrollStepX;

                if (isRtl) {
                    scrollAmount = scrollForward ? -scrollStepX : scrollStepX;
                }

                current.scrollBy({left: scrollAmount, behavior: 'smooth'});
            } else {
                const scrollAmount =
                    direction === ScrollDirection.forward ? scrollStepY : -scrollStepY;
                current.scrollBy({top: scrollAmount, behavior: 'smooth'});
            }
        }
    };

    render() {
        const {direction, componentClasses, sidePadding, height, isRtl, showArrows} = this.props;
        const {handleSize} = this.state;

        return (
            <div
                ref={this.componentRef}
                style={{[isRtl ? 'paddingRight' : 'paddingLeft']: sidePadding, height}}
                className={cx(styles.component, componentClasses, {
                    [styles.horizontal]: direction === 'horizontal',
                    [styles.vertical]: direction === 'vertical',
                    [styles.isRtl]: isRtl
                })}
            >
                {showArrows && (
                    <div
                        className={cx(styles.arrow, styles.arrowBack)}
                        onClick={this.onArrowClick}
                        data-direction={ScrollDirection.backward}
                    />
                )}

                <div className={cx(styles.inner, {[styles.hasArrows]: showArrows})}>
                    <div className={styles.rail} onClick={this.onRailClick} ref={this.railRef} />
                    <div
                        className={cx(styles.handle, {[styles.dragging]: this.state.isDragging})}
                        ref={this.handleRef}
                        onMouseDown={this.handleThumbMousedown}
                        style={{[this.isHorizontal ? 'width' : 'height']: handleSize}}
                    />
                </div>

                {showArrows && (
                    <div
                        className={cx(styles.arrow, styles.arrowForward)}
                        onClick={this.onArrowClick}
                        data-direction={ScrollDirection.forward}
                    />
                )}
            </div>
        );
    }
}
